import React, { type MouseEvent, useCallback } from 'react';

import Button from '@atlaskit/button';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useChildIssuesLimit } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { getChildIssuesLimitExceededAnalyticsCount } from '@atlassian/jira-issue-view-common-types/src/children-issues-type.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import type { ChildIssuesPanelType } from '../../../common/types.tsx';
import { generateBulkEditLink } from '../../heading/meatballs/src/utils.tsx';
import { useChildIssuesSortBy } from '../../../controllers/main.tsx';
import { BULK_EDIT_BUTTON_TEST_ID } from '../constants/index.tsx';
import messages from './messages.tsx';

type Props = {
	hasExceededIssuesLimitInitialLoad: boolean;
	childIssuesPanelType: ChildIssuesPanelType;
	parentKey: IssueKey;
};

export const BulkEditButton = ({
	hasExceededIssuesLimitInitialLoad,
	childIssuesPanelType,
	parentKey,
}: Props) => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();

	const [sortByValue] = useChildIssuesSortBy();
	const childIssuesLimit = useChildIssuesLimit();

	const bulkEditLink = generateBulkEditLink(
		parentKey,
		childIssuesPanelType,
		hasExceededIssuesLimitInitialLoad,
		sortByValue || undefined,
	);

	const onBulkEditClickedAnalytics = useCallback(
		(event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'bulkEdit', {
				issueCount: getChildIssuesLimitExceededAnalyticsCount(childIssuesLimit),
			});

			// Ignore cmd or ctrl clicks - allow user to open in new tab
			if (!event.metaKey && !event.ctrlKey) {
				event.preventDefault();
				push(bulkEditLink);
			}
		},
		[bulkEditLink, childIssuesLimit, push],
	);

	return (
		<Button
			appearance="default"
			href={bulkEditLink}
			onClick={onBulkEditClickedAnalytics}
			testId={BULK_EDIT_BUTTON_TEST_ID}
			interactionName="auto-generated.jira-issue-view-common-views.child-issues-panel.view.child-issues-limit-panel.button.bulk-edit"
		>
			{formatMessage(messages.bulkEditButton)}
		</Button>
	);
};
